import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  Search,
  Sparkles,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  Play,
  Check,
  Eye,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import NotificationDrawer from "../components/NotificationDrawer";
import { Mascot } from "../components/Mascot";
import { Post, Notification, cleanAuthorName, getAuthorDisplayName } from "../types";
import { supabaseService } from "../supabaseService";
import CommentSection from "../components/CommentSection";
import AdUnit from "../components/AdUnit";
import { useLike } from "../hooks/useLike";
import SkeletonPostCard from "../components/SkeletonPostCard";
import NetworkError from "../components/NetworkError";
import { extractEntityId, getAuthorSlug, getPostSlug, getPostReadingPath, slugify } from "../lib/authorSlug";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DynamicMeta } from "../components/DynamicMeta";
import { vibrateNative } from "../lib/mobileBridge";
import { buildPostShareData, shareOrCopy, shareOutcomeToast } from "../lib/shareLink";

const timeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + "h ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + "m ago";
  return "just now";
};

interface PoemSlideProps {
  post: Post;
  isActive: boolean;
}

const PoemTikTokSlide: React.FC<PoemSlideProps> = ({ post, isActive }) => {
  const {
    liked,
    setLiked,
    likesCount,
    setLikesCount,
    forceLike,
    toggleLike,
    isAnimating,
  } = useLike(post.id, "post", false, post.likes_count);
  const [saved, setSaved] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comments_count || 0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showHeartPop, setShowHeartPop] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const lastTap = useRef<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    supabaseService.isPostSaved(post.id).then(setSaved);
    supabaseService.isLiked(post.id).then(setLiked);
    supabaseService.isFollowing(post.user_id).then(setIsFollowing);

    // Fetch latest counts to prevent stale prop issues
    supabaseService.getPost(post.id).then((freshPost) => {
      if (freshPost) {
        setLikesCount((prev) => Math.max(prev, freshPost.likes_count || 0));
        setCommentCount((prev) => Math.max(prev, freshPost.comments_count || 0));
      }
    });
  }, [post.id]);

  const handleDoubleTap = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      if (!liked) {
        forceLike();
      }
      vibrateNative(16);
      setShowHeartPop(true);
      setTimeout(() => setShowHeartPop(false), 800);
    }
    lastTap.current = now;
  };

  const handleToggleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLike();
  };

  const handleToggleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const newState = await supabaseService.toggleSavePost(post.id);
    setSaved(newState);
  };

  const handleToggleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const nextFollowingState = !isFollowing;
    try {
      await supabaseService.toggleFollow(post.user_id, nextFollowingState);
      setIsFollowing(nextFollowingState);
    } catch (err) {
      console.warn("Failed to toggle follow:", err);
    }
  };



  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const outcome = await shareOrCopy(buildPostShareData(post));
    if (outcome === 'dismissed') return;
    const shareToast = shareOutcomeToast(outcome);
    if (shareToast) {
      setToast(shareToast.message);
      setTimeout(() => setToast(null), 2500);
    }
  };;

  const authorDisplay = getAuthorDisplayName(post);

  const navigateToAuthor = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/author/${getAuthorSlug(authorDisplay, post.user_id)}`);
  };

  const touchStart = useRef<number>(0);
  const touchY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
    touchY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStart.current - touchEndX;
    const diffY = Math.abs(touchY.current - touchEndY);

    // Swipe left (more than 60px) and not too much vertical movement
    if (diffX > 60 && diffY < 50) {
      navigate(`/author/${getAuthorSlug(authorDisplay, post.user_id)}`);
    }
  };

  const hasMedia = post.media_url || post.background_url;

  return (
    <div
      onClick={handleDoubleTap}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className="h-[100dvh] w-full snap-start snap-always relative bg-black flex items-center justify-center overflow-hidden select-none"
    >
      {/* Background Media / Gradient */}
      {hasMedia ? (
        <img
          src={post.media_url || post.background_url}
          alt={post.title || "Poem background"}
          className="absolute inset-0 w-full h-full object-cover opacity-50 select-none pointer-events-none"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-slate-950 to-black opacity-95 pointer-events-none" />
      )}

      {/* Top & Bottom gradient overlays for high-contrast text readability */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 via-black/40 to-transparent z-0 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black via-black/70 to-transparent z-0 pointer-events-none" />

      {/* Center Poem Lines Area */}
      <div className="absolute inset-0 z-10 flex flex-col justify-center items-center px-6 sm:px-12 md:px-20 pt-20 pb-36 overflow-hidden pointer-events-none">
        <div
          className="w-full max-w-xl max-h-[72%] overflow-y-auto no-scrollbar py-4 px-4 pointer-events-auto overscroll-contain text-left rounded-2xl bg-black/20 backdrop-blur-[2px]"
          style={{
            maskImage:
              "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent, black 8%, black 92%, transparent)",
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          {post.title && (
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={isActive ? { opacity: 1, y: 0 } : {}}
              className="text-2xl sm:text-3xl md:text-4xl font-extrabold serif text-white tracking-tight leading-tight mb-5 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
            >
              {post.title}
            </motion.h2>
          )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isActive ? { opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
            className={`serif text-base sm:text-lg md:text-xl text-white/95 leading-[1.9] select-text break-words drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] ${
              post.type === "poem"
                ? "poem-content whitespace-pre-line"
                : "leading-[1.8] [&_p]:mb-4 [&_br]:mb-2"
            }`}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(post.content),
            }}
          />
        </div>
      </div>

      {/* Right Action Bar (Avatar, Like, Comment, Bookmark, Share) */}
      <div className="absolute right-3.5 sm:right-5 bottom-28 md:bottom-24 z-20 flex flex-col items-center gap-4.5 pointer-events-auto">
        {/* Author Avatar with Follow Badge */}
        <div
          className="relative group cursor-pointer mb-1"
          onClick={navigateToAuthor}
          title={cleanAuthorName(post.author_name)}
        >
          <img
            src={
              post.author_avatar ||
              `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.user_id}`
            }
            className="w-12 h-12 rounded-full border-2 border-white object-cover bg-white shadow-lg group-hover:scale-105 transition-transform"
            referrerPolicy="no-referrer"
            alt={post.author_name}
          />
          {isFollowing ? (
            <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-indigo-500 rounded-full w-5 h-5 flex items-center justify-center border-2 border-white shadow-md">
              <Check size={10} className="text-white" strokeWidth={4} />
            </div>
          ) : (
            <button
              onClick={handleToggleFollow}
              className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-rose-500 hover:bg-rose-600 rounded-full w-5 h-5 flex items-center justify-center border-2 border-white text-white font-black text-[12px] pb-[1px] active:scale-90 transition-transform shadow-md"
              title="Follow Author"
            >
              +
            </button>
          )}
        </div>

        {/* Like Button */}
        <div
          className="flex flex-col items-center gap-1 cursor-pointer group"
          onClick={handleToggleLike}
        >
          <div className="p-2.5 rounded-full bg-black/30 backdrop-blur-md group-active:scale-90 transition-transform hover:bg-black/50">
            <Heart
              size={28}
              className={`transition-colors ${
                liked
                  ? "fill-rose-500 text-rose-500"
                  : "text-white group-hover:text-rose-400"
              } ${isAnimating ? "animate-ping" : ""}`}
            />
          </div>
          <span className="text-white text-[11px] font-bold drop-shadow-md">
            {likesCount > 0 ? likesCount.toLocaleString() : "0"}
          </span>
        </div>

        {/* Comments Button */}
        <div
          className="flex flex-col items-center gap-1 cursor-pointer group"
          onClick={(e) => {
            e.stopPropagation();
            setShowComments(true);
          }}
        >
          <div className="p-2.5 rounded-full bg-black/30 backdrop-blur-md group-active:scale-90 transition-transform hover:bg-black/50">
            <MessageCircle size={28} className="fill-white/20 text-white group-hover:text-indigo-300" />
          </div>
          <span className="text-white text-[11px] font-bold drop-shadow-md">
            {commentCount > 0 ? commentCount.toLocaleString() : "0"}
          </span>
        </div>

        {/* Bookmark / Save Button */}
        <div
          className="flex flex-col items-center gap-1 cursor-pointer group"
          onClick={handleToggleSave}
        >
          <div className="p-2.5 rounded-full bg-black/30 backdrop-blur-md group-active:scale-90 transition-transform hover:bg-black/50">
            <Bookmark
              size={28}
              className={saved ? "fill-amber-400 text-amber-400" : "text-white group-hover:text-amber-300"}
            />
          </div>
          <span className="text-white text-[11px] font-bold drop-shadow-md">
            {saved ? "Saved" : "Save"}
          </span>
        </div>

        {/* Share Button */}
        <div
          className="flex flex-col items-center gap-1 cursor-pointer group"
          onClick={handleShare}
        >
          <div className="p-2.5 rounded-full bg-black/30 backdrop-blur-md group-active:scale-90 transition-transform hover:bg-black/50">
            <Share2 size={28} className="fill-white/20 text-white group-hover:text-blue-300" />
          </div>
          <span className="text-white text-[11px] font-bold drop-shadow-md">
            Share
          </span>
        </div>
      </div>

      {/* Bottom Info Area */}
      <div className="absolute left-4 bottom-24 md:bottom-20 right-20 z-20 flex flex-col gap-2 pointer-events-none">
        {/* Author Name with Blue Verified Badge */}
        <div
          className="flex items-center gap-2 cursor-pointer pointer-events-auto"
          onClick={navigateToAuthor}
        >
          <span className="text-white font-bold text-[16px] sm:text-[17px] drop-shadow-md hover:underline">
            {authorDisplay}
          </span>
          <div className="w-3.5 h-3.5 rounded-full bg-blue-500 flex items-center justify-center shadow-sm">
            <svg
              className="w-2.5 h-2.5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-[11px] text-white/70 font-medium drop-shadow-sm">
            • {timeAgo(post.created_at)}
          </span>
        </div>

        {/* Poem Title and Hashtags (Author's Choice) */}
        <p className="text-white/95 text-[13px] sm:text-[14px] drop-shadow-md line-clamp-2 pr-4 font-serif">
          {post.title ? `"${post.title}"` : ""}
          {post.hashtag && (
            <span className="font-sans font-bold text-indigo-300 opacity-90 ml-1 text-xs">
              {post.hashtag}
            </span>
          )}
          {post.themes &&
            post.themes.slice(0, 3).map((t) => (
              <span
                key={t}
                className="font-sans font-bold text-indigo-300 opacity-90 ml-1 text-xs"
              >
                #{t.replace(/^#/, '')}
              </span>
            ))}
        </p>

        {/* Music / Audio Indicator Pill */}
        <div className="flex items-center gap-2 mt-0.5">
          <div className="bg-white/15 backdrop-blur-md rounded-lg px-2.5 py-1 flex items-center gap-1.5 border border-white/10 shadow-sm">
            <svg
              className="w-3 h-3 text-white animate-pulse"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M9 18V5l12-2v13"></path>
              <circle cx="6" cy="18" r="3"></circle>
              <circle cx="18" cy="16" r="3"></circle>
            </svg>
            <span className="text-white text-[11px] font-bold truncate max-w-[200px]">
              Original Sound - {authorDisplay}
            </span>
          </div>
        </div>
      </div>

      {/* double tap heart animation pop */}
      <AnimatePresence>
        {showHeartPop && (
          <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none select-none">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [1, 1.4, 1], opacity: [0, 1, 0] }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="text-rose-500 fill-rose-500"
            >
              <Heart size={120} fill="currentColor" strokeWidth={1} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* toast popover */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute left-1/2 top-4 -translate-x-1/2 px-4 py-2 bg-indigo-600 text-white rounded-full text-xs font-bold shadow-lg z-[99] pointer-events-none"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comments overlay bottom panel sheet */}
      <AnimatePresence>
        {showComments && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowComments(false)}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed bottom-16 md:bottom-0 left-0 right-0 h-[52vh] md:h-[65vh] max-w-2xl mx-auto bg-slate-900 border-t border-slate-800 rounded-t-[28px] md:rounded-t-[32px] z-[60] flex flex-col overflow-hidden pointer-events-auto shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <span className="font-bold text-sm text-indigo-200">
                  Comments ({commentCount})
                </span>
                <button
                  onClick={() => setShowComments(false)}
                  className="p-1 px-3 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-300 font-bold text-xs"
                >
                  Close
                </button>
              </div>
              <div className="flex-1 overflow-y-auto no-scrollbar">
                <CommentSection
                  resourceId={post.id}
                  resourceType="post"
                  maxHeight="100%"
                  onCommentAdded={() => setCommentCount((prev) => prev + 1)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const MOODS = [
  { id: "All", label: "All Moods", icon: "✨" },
  { id: "Melancholy", label: "Melancholy", icon: "🖤" },
  { id: "Love & Romance", label: "Romance", icon: "💖" },
  { id: "Hope & Inspiration", label: "Inspiration", icon: "✨" },
  { id: "Mysterious & Dark", label: "Dark", icon: "🔮" },
  { id: "Nature & Peace", label: "Nature", icon: "🌿" },
  { id: "Cosmic & Sci-Fi", label: "Cosmic", icon: "🌌" },
  { id: "Philosophy & Life", label: "Philosophy", icon: "⏳" },
];

const moodKeywords: Record<string, string[]> = {
  "Melancholy": ["sad", "cry", "grief", "blue", "dark", "lonely", "shadow", "tears", "pain", "rain", "cold", "broken", "empty", "ache", "loss", "sorrow", "weep", "mourn", "gloom", "alone"],
  "Love & Romance": ["love", "heart", "kiss", "sweet", "romance", "embrace", "dear", "lover", "touch", "beauty", "soul", "eyes", "smile", "warmth", "passion", "cherish", "gaze", "forever"],
  "Hope & Inspiration": ["hope", "rise", "light", "dream", "inspire", "future", "sun", "shining", "bright", "dawn", "believe", "strong", "overcome", "faith", "growth", "promise", "glow", "soar"],
  "Mysterious & Dark": ["mystery", "dark", "shadow", "ghost", "haunt", "secret", "midnight", "whisper", "fog", "moon", "silent", "grave", "cryptic", "veil", "night", "phantom", "obscure", "gothic"],
  "Nature & Peace": ["nature", "peace", "calm", "tree", "river", "leaf", "wind", "forest", "mountain", "garden", "flower", "grass", "sky", "ocean", "sea", "quiet", "serene", "breeze", "woods", "stream"],
  "Cosmic & Sci-Fi": ["cosmic", "space", "star", "galaxy", "universe", "planet", "moon", "infinite", "astral", "rocket", "future", "void", "comet", "orbit", "constellation", "nebula"],
  "Philosophy & Life": ["philosophy", "life", "mind", "truth", "wisdom", "exist", "thought", "time", "reason", "soul", "meaning", "question", "journey", "destiny", "death", "fate", "eternity", "ponder"],
};

const Feed: React.FC = () => {
  const queryClient = useQueryClient();
  const [feedType, setFeedType] = useState<"for_you" | "following">("for_you");

  const { data: userProfile } = useQuery({
    queryKey: ["profile", "me"],
    queryFn: async () => supabaseService.getProfile(),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: queryPosts,
    isLoading: isFeedLoading,
    isError: feedError,
    refetch: refetchFeed,
  } = useQuery({
    queryKey: ["feed", "poem", feedType],
    queryFn: async () => {
      const data = feedType === 'for_you' 
        ? await supabaseService.getPersonalizedFeed("poem") 
        : await supabaseService.getFollowingFeed("poem");
      return Array.from(new Map(data.map((p) => [p.id, p])).values());
    },
    staleTime: 2 * 60 * 1000,
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [directPost, setDirectPost] = useState<Post | null>(null);
  const [directPostLoading, setDirectPostLoading] = useState(false);
  const [directPostError, setDirectPostError] = useState(false);
  const [directPostRetry, setDirectPostRetry] = useState(0);
  const [selectedMood, setSelectedMood] = useState<string>("All");
  const [showSkeleton, setShowSkeleton] = useState(false);
  const loading = isFeedLoading && posts.length === 0 && showSkeleton;

  useEffect(() => {
    if (isFeedLoading && posts.length === 0) {
      const timer = setTimeout(() => setShowSkeleton(true), 150);
      return () => clearTimeout(timer);
    }
    setShowSkeleton(false);
  }, [isFeedLoading, posts.length]);
  const isError = feedError;
  const [isAppending, setIsAppending] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { slug, postId, author, title } = useParams<{ slug?: string; postId?: string; author?: string; title?: string }>();
  const searchParams = new URLSearchParams(location.search);
  const hashId = typeof window !== 'undefined' && window.location.hash.startsWith('#/poem') ? extractEntityId(window.location.hash) : '';
  const directParam = postId || slug || author || title || searchParams.get('poem') || searchParams.get('post');
  const targetId = directParam ? extractEntityId(directParam) : hashId;

  useEffect(() => {
    if (targetId) {
      let cancelled = false;
      setDirectPost(null);
      setDirectPostLoading(true);
      setDirectPostError(false);
      supabaseService.getPost(targetId).then((targetPost) => {
        if (cancelled) return;
        if (targetPost?.type && targetPost.type !== 'poem') {
          navigate(getPostReadingPath(targetPost.id, targetPost.type, targetPost.title, targetPost.author_name), { replace: true });
          return;
        }
        if (targetPost) {
          setDirectPost(targetPost);
          setPosts((prev) => {
            const filtered = prev.filter(p => p.id !== targetPost.id);
            return [targetPost, ...filtered];
          });
          setActiveIndex(0);
          const authorDisplay = getAuthorDisplayName(targetPost);
          const authorSlug = slugify(authorDisplay) || 'author';
          const titleSlug = slugify(targetPost.title) || 'poem';
          const canonicalPath = `/poem/${authorSlug}/${titleSlug}-${targetPost.id}`;
          if (typeof window !== 'undefined' && location.pathname !== canonicalPath) {
            window.history.replaceState(window.history.state, '', canonicalPath);
          }
          if (targetPost.title) {
            document.title = `${targetPost.title} by ${authorDisplay} | Literally`;
          }
          if (containerRef.current) {
            containerRef.current.scrollTo({ top: 0, behavior: "auto" });
          }
        } else {
          setDirectPostError(true);
        }
        setDirectPostLoading(false);
      }).catch((error) => {
        if (!cancelled) {
          console.error('Could not load the requested poem:', error);
          setDirectPostLoading(false);
          setDirectPostError(true);
        }
      });
      return () => {
        cancelled = true;
      };
    } else {
      setDirectPost(null);
      setDirectPostLoading(false);
      setDirectPostError(false);
    }
  }, [targetId, directParam, slug, navigate, directPostRetry]);

  const isFetchingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewedPostsRef = useRef<Set<string>>(new Set());

  // Raw post list guaranteeing directPost is placed at top if present
  const rawPostsList = directPost
    ? [directPost, ...posts.filter((post) => post.id !== directPost.id)]
    : posts;

  // Strict map deduplication to prevent duplicate keys or repeating poem slides
  const deduplicatedPosts = Array.from(
    new Map(rawPostsList.map((p) => [p.id, p])).values()
  );

  // Filter posts dynamically by selectedMood with smart fallbacks
  const filteredPosts = deduplicatedPosts.filter((post) => {
    if (selectedMood === "All") return true;

    // Match by post.theme (niche/mood saved by the author)
    if (post.theme && post.theme.toLowerCase() === selectedMood.toLowerCase()) return true;

    // Match by post.themes array
    if (post.themes && post.themes.some((t) => t.toLowerCase() === selectedMood.toLowerCase())) return true;

    // Fallback classification using semantic keyword matching
    const contentLower = (post.content || "").toLowerCase();
    const titleLower = (post.title || "").toLowerCase();
    const keywords = moodKeywords[selectedMood];
    if (keywords) {
      return keywords.some(
        (keyword) => contentLower.includes(keyword) || titleLower.includes(keyword)
      );
    }

    return false;
  });

  // Reset viewport and active post index when the selected filter changes
  useEffect(() => {
    setActiveIndex(0);
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [selectedMood]);

  useEffect(() => {
    document.title = "Literally | Poems";
    loadNotifications();

    // Subscribe to Real-time Notifications
    const unsubscribe = supabaseService.subscribeToNotifications(
      (newNote: any) => {
        setNotifications((prev) => {
          if (prev.some((n) => n.id === newNote.id)) return prev;
          return [newNote, ...prev];
        });
        setHasUnread(true);
      },
    );

    const handleNavReload = (e: any) => {
      if (e.detail?.path === "/") {
        if (containerRef.current) {
          containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
        refetchFeed();
      }
    };
    window.addEventListener("nav-reload", handleNavReload);

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
      window.removeEventListener("nav-reload", handleNavReload);
    };
  }, []);

  useEffect(() => {
    setPosts([]);
    viewedPostsRef.current.clear();
    setActiveIndex(0);
  }, [feedType]);

  useEffect(() => {
    if (queryPosts && queryPosts.length > 0) {
      setPosts((prev) => {
        if (prev.length === 0) return queryPosts;
        const prevIds = new Set(prev.map((p) => p.id));
        const filteredNewPosts = queryPosts.filter((p) => !prevIds.has(p.id));
        if (filteredNewPosts.length === 0) return prev;
        return [...prev, ...filteredNewPosts];
      });

      if (queryPosts[0]?.user_id) {
        const firstPost = queryPosts[0];
        supabaseService.incrementPostViews(firstPost.id, { authorId: firstPost.user_id }).then((incremented) => {
          if (incremented && firstPost.user_id) {
            supabaseService.incrementPoemViews(firstPost.user_id);
          }
        }).catch(() => {});
      }
    }
  }, [queryPosts]);

  const loadMorePosts = async () => {
    if (isAppending || isFetchingRef.current) return;
    setIsAppending(true);
    isFetchingRef.current = true;
    try {
      const currentOffset = posts.length;
      const data = feedType === "for_you"
        ? await supabaseService.getPersonalizedFeed("poem", 50, currentOffset)
        : await supabaseService.getFollowingFeed("poem", 50, currentOffset);
      if (data && data.length > 0) {
        setPosts((prev) => {
          const currentIds = new Set(prev.map((p) => p.id));
          const uniqueNewData = data.filter((p) => !currentIds.has(p.id));
          return uniqueNewData.length > 0 ? [...prev, ...uniqueNewData] : prev;
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsAppending(false);
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 1500);
    }
  };

  const loadNotifications = async () => {
    const notes = await supabaseService.getNotifications();
    const unique = Array.from(new Map(notes.map((n) => [n.id, n])).values());
    setNotifications(unique);
    setHasUnread(unique.some((n) => !n.is_read));
  };

  const handleOpenNotifications = async () => {
    setShowNotifications(true);
    if (hasUnread) {
      await supabaseService.markNotificationsAsRead();
      setHasUnread(false);
    }
  };

  const scrollToNext = () => {
    if (!containerRef.current) return;
    const { scrollTop, clientHeight } = containerRef.current;
    containerRef.current.scrollTo({
      top: scrollTop + clientHeight,
      behavior: 'smooth'
    });
  };

  const scrollToPrev = () => {
    if (!containerRef.current) return;
    const { scrollTop, clientHeight } = containerRef.current;
    containerRef.current.scrollTo({
      top: Math.max(0, scrollTop - clientHeight),
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === "PageDown" || e.key === "ArrowDown" || e.key === "j" || e.key === " ") {
        if (e.key === " " && target.tagName === "BUTTON") return;
        e.preventDefault();
        scrollToNext();
      } else if (e.key === "PageUp" || e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        scrollToPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleContainerScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, clientHeight } = containerRef.current;
    if (clientHeight === 0) return;
    const index = Math.round(scrollTop / clientHeight);
    if (index >= 0 && index < filteredPosts.length && index !== activeIndex) {
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    if (filteredPosts.length > 0 && activeIndex >= 0 && activeIndex < filteredPosts.length) {
      const activePost = filteredPosts[activeIndex];
      if (activePost && activePost.user_id) {
        supabaseService.incrementPostViews(activePost.id, { authorId: activePost.user_id }).then((incremented) => {
          if (incremented && activePost.user_id) {
            supabaseService.incrementPoemViews(activePost.user_id);
          }
        }).catch(() => {});

        // Keep loading additional poems as we reach near the bottom of feed buffer (TikTok logic)
        if (activeIndex >= filteredPosts.length - 2) {
          loadMorePosts();
        }
      }
    }
  }, [activeIndex, filteredPosts]);

  const activePost = filteredPosts[activeIndex];
  const authorSlug = activePost ? (activePost.author_name || 'author').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : 'author';
  const titleSlug = activePost ? (activePost.title || 'poem').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : 'poem';
  const currentUrl = activePost ? `${window.location.origin}/poem/${authorSlug}/${titleSlug}-${activePost.id}` : undefined;

  return (
    <div className="w-full h-screen bg-white dark:bg-slate-950 relative overscroll-none select-none">
      <DynamicMeta
        title={activePost?.title || (activePost?.type ? activePost.type.charAt(0).toUpperCase() + activePost.type.slice(1) : 'Poem')}
        description={activePost?.content}
        image={activePost?.media_url}
        url={currentUrl}
        type="article"
        authorName={cleanAuthorName(activePost?.author_name)}
        publishedTime={activePost?.created_at}
      />
      {/* Fixed Transparent Header Floating over full screen */}
      <header className="fixed top-0 left-0 right-0 md:left-60 z-40 flex items-center justify-between p-4 bg-gradient-to-b from-white/90 via-white/50 to-transparent dark:from-black/90 dark:via-black/50 pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <div
            onClick={() => navigate("/profile")}
            className="cursor-pointer relative"
          >
            {userProfile ? (
              <img
                src={userProfile.avatar_url}
                referrerPolicy="no-referrer"
                className="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-white/50 shadow-sm"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-800 animate-pulse" />
            )}
          </div>
          <div>
            <h1 className="text-xl font-black italic tracking-tighter poppins text-slate-900 dark:text-white drop-shadow-sm">
              Literally.
            </h1>
            <p className="text-[9px] text-slate-500 dark:text-slate-300 font-bold uppercase tracking-wider drop-shadow-sm">
              Poems
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center pointer-events-auto">
          <button
            onClick={() => navigate("/search")}
            className="w-9 h-9 bg-white/60 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-full flex items-center justify-center text-slate-700 dark:text-white hover:bg-white dark:hover:bg-black/60 transition-all backdrop-blur-md shadow-sm"
          >
            <Search size={18} />
          </button>
          <button
            onClick={handleOpenNotifications}
            className="w-9 h-9 bg-white/60 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-full flex items-center justify-center text-slate-700 dark:text-white relative hover:bg-white dark:hover:bg-black/60 transition-all backdrop-blur-md shadow-sm"
          >
            <Bell size={18} />
            {hasUnread && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full shadow-[0_0_8px_rgba(244,63,94,0.6)]" />
            )}
          </button>
        </div>
      </header>
      {/* Feed Type Tabs */}
      <div className="fixed top-[64px] left-0 right-0 md:left-60 z-40 flex justify-center pointer-events-auto">
        <div className="flex bg-slate-200/50 dark:bg-white/10 border border-slate-200/50 dark:border-white/10 backdrop-blur-md rounded-full p-1">
          <button
            onClick={() => setFeedType('for_you')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${feedType === 'for_you' ? 'bg-white dark:bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 dark:text-white/80 dark:hover:text-white'}`}
          >
            For You
          </button>
          <button
            onClick={() => setFeedType('following')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${feedType === 'following' ? 'bg-white dark:bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 dark:text-white/80 dark:hover:text-white'}`}
          >
            Following
          </button>
        </div>
      </div>

      {/* Main Snap scroll viewport */}
      <div className="w-full h-full pt-0 pb-0">
        {loading || directPostLoading ? (
          <div className="w-full h-full flex flex-col pt-24 space-y-6">
            <SkeletonPostCard />
            <SkeletonPostCard />
            <SkeletonPostCard />
          </div>
        ) : directPostError ? (
          <div className="w-full h-full flex items-center justify-center">
            <NetworkError
              title="Could not open this poem"
              message="We couldn't load the requested poem. Please try again."
              onRetry={() => setDirectPostRetry((attempt) => attempt + 1)}
            />
          </div>
        ) : isError && posts.length === 0 ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-left px-6 bg-slate-50 dark:bg-slate-950">
            <Mascot expression="happy" size={180} />
            <h3 className="text-lg font-bold font-serif text-slate-800 dark:text-slate-100 mt-4 mb-2">
              Feed is catching up...
            </h3>
            <p className="text-slate-500 dark:text-slate-400 font-serif max-w-sm text-xs leading-relaxed mb-6">
              Could not load recent posts. Tap below to refresh your feed!
            </p>
            <button
              onClick={() => refetchFeed()}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs font-bold transition-all shadow-md active:scale-95 flex items-center gap-2"
            >
              <RefreshCw size={14} className="animate-spin" />
              Refresh Feed
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-left px-6 bg-slate-50 text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Sparkles className="w-12 h-12 text-indigo-400 animate-pulse mb-4" />
            <p className="font-serif max-w-sm text-sm leading-relaxed">
              The literary ink is resting. Publish your first poem in the Studio
              to set the feed alight!
            </p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-left px-6 bg-slate-50 text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Sparkles className="w-12 h-12 text-indigo-400 animate-pulse mb-4" />
            <p className="font-serif max-w-sm text-sm leading-relaxed mb-6">
              No poems found matching <span className="font-bold text-indigo-500">"{selectedMood}"</span> yet.
              Be the first to publish a poem in this mood and set the feed alight!
            </p>
            <button
              onClick={() => navigate("/studio")}
              className="px-6 py-2.5 bg-indigo-600 text-white font-bold text-xs rounded-full hover:bg-indigo-700 transition-all shadow-md active:scale-95"
            >
              Go to Studio
            </button>
          </div>
        ) : (
          <div
            ref={containerRef}
            onScroll={handleContainerScroll}
            className="w-full h-full overflow-y-scroll snap-y snap-mandatory scroll-smooth bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white select-none no-scrollbar"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {(() => {
                const uniquePosts = Array.from(new Map(filteredPosts.map((p) => [p.id, p])).values());
                const feedItems: any[] = [];
                uniquePosts.forEach((post, i) => {
                    feedItems.push({ type: 'poem', post });
                    if ((i + 1) % 3 === 0) {
                        feedItems.push({ type: 'ad', id: `poemand-${i}` });
                    }
                });

                return feedItems.map((item, index) => {
                    if (item.type === 'poem') {
                        return (
                            <PoemTikTokSlide 
                                key={`slide-${item.post.id}-${index}`} 
                                post={item.post} 
                                isActive={index === activeIndex} 
                            />
                        );
                    } else {
                        return (
                          <div key={`ad-container-${item.id}-${index}`} className="w-full h-full snap-start snap-always flex flex-col justify-center items-center overflow-hidden px-4 md:px-0 bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="w-full mx-auto max-w-[420px]">
                              <AdUnit
                                className="w-full shadow-lg"
                                slotId={item.id}
                              />
                              <div className="text-left font-bold text-slate-400 uppercase tracking-widest text-[10px] mt-6 flex flex-col items-center gap-2">
                                <span>Swipe up to continue</span>
                                <div className="w-1 h-8 bg-gradient-to-b from-slate-300 to-transparent dark:from-slate-700 rounded-full animate-pulse" />
                              </div>
                            </div>
                          </div>
                        );
                    }
                });
            })()}

            {isAppending && (
              <div className="w-full h-full snap-start flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop Up / Down Float Navigation Controls */}
      <div className="hidden md:flex flex-col gap-2 absolute right-6 bottom-8 z-30 pointer-events-auto">
        <button
          onClick={scrollToPrev}
          title="Previous poem (Page Up / ↑)"
          className="w-11 h-11 rounded-full bg-white/80 dark:bg-slate-900/80 hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-white/10 shadow-lg backdrop-blur-md flex items-center justify-center text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all hover:scale-105 active:scale-95 group"
        >
          <ChevronUp size={20} className="transition-transform group-hover:-translate-y-0.5" />
        </button>
        <button
          onClick={scrollToNext}
          title="Next poem (Page Down / ↓)"
          className="w-11 h-11 rounded-full bg-white/80 dark:bg-slate-900/80 hover:bg-white dark:hover:bg-slate-800 border border-slate-200 dark:border-white/10 shadow-lg backdrop-blur-md flex items-center justify-center text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all hover:scale-105 active:scale-95 group"
        >
          <ChevronDown size={20} className="transition-transform group-hover:translate-y-0.5" />
        </button>
      </div>

      <AnimatePresence>
        {showNotifications && (
          <NotificationDrawer
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
            notifications={notifications}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Feed;
